import { useEffect } from 'react';

/**
 * Shared hook that derives hasValidated, isApproved, and anyVariantHasErrors
 * across ALL saved variants. Used by both social (PreviewAndAdDetails) and
 * search (SearchOrchestrator) so the Approve button is only enabled when
 * every version has been validated.
 */

interface Asset {
  variant_number?: number | null;
  validation_result?: { is_valid?: boolean } | null;
  status?: string | null;
  storage_path?: string | null;
}

interface Callbacks {
  onHasValidatedChange?: (v: boolean) => void;
  onIsApprovedChange?: (v: boolean) => void;
  onAnyVariantHasErrorsChange?: (v: boolean) => void;
}

interface Options {
  /** Unsaved variant numbers — if any exist the bundle is incomplete. */
  unsavedNewVariants?: number[];
  /** Social assets default variant_number to 1 when null; search always has it. */
  defaultVariantNumber?: number;
  /** Social requires storage_path for approval; search doesn't. */
  requireStoragePath?: boolean;
}

export function useCrossVariantStatus(
  assets: Asset[] | undefined,
  callbacks: Callbacks,
  options: Options = {},
) {
  const { unsavedNewVariants = [], defaultVariantNumber = 1, requireStoragePath = false } = options;

  // Stable serialisation of unsavedNewVariants for the dep array
  const unsavedKey = unsavedNewVariants.join(',');

  useEffect(() => {
    if (!assets) return;

    const savedVariantNumbers = [
      ...new Set(assets.map((a) => a.variant_number ?? defaultVariantNumber)),
    ];

    // If there are unsaved new variants, the bundle is incomplete.
    const hasUnsaved = unsavedNewVariants.some((n) => !savedVariantNumbers.includes(n));
    if (savedVariantNumbers.length === 0 || hasUnsaved) {
      callbacks.onHasValidatedChange?.(false);
      callbacks.onIsApprovedChange?.(false);
      callbacks.onAnyVariantHasErrorsChange?.(false);
      return;
    }

    const allValidated = savedVariantNumbers.every((vn) => {
      const va = assets.filter((a) => (a.variant_number ?? defaultVariantNumber) === vn);
      return va.some((a) => a.validation_result != null);
    });

    const anyVariantHasErrors = savedVariantNumbers.some((vn) => {
      const va = assets.filter((a) => (a.variant_number ?? defaultVariantNumber) === vn);
      return va.some((a) => a.validation_result && a.validation_result.is_valid === false);
    });

    const allApproved = savedVariantNumbers.every((vn) => {
      const va = assets.filter((a) => (a.variant_number ?? defaultVariantNumber) === vn);
      return (
        va.some((a) => a.status === 'approved' && (!requireStoragePath || !!a.storage_path)) &&
        va.some((a) => a.validation_result != null)
      );
    });

    callbacks.onHasValidatedChange?.(allValidated);
    callbacks.onIsApprovedChange?.(allApproved);
    callbacks.onAnyVariantHasErrorsChange?.(anyVariantHasErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, unsavedKey, defaultVariantNumber, requireStoragePath]);
}
